import { useState, useEffect } from "react";
import { toast } from "sonner";
import { useUserSettings } from "../components/settings/UserSettingsContext";
import { useTheme } from "../components/theme/ThemeProvider";
import { getAvailableTimezones, getAvailableCurrencies, extractErrorMessage } from "../lib/api";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import {
  User,
  Globe,
  Calendar,
  Clock,
  DollarSign,
  Palette,
  Accessibility,
  RotateCcw,
  Sun,
  Moon,
  Monitor,
  Check,
  X,
  Loader2,
  Eye,
  Type,
} from "lucide-react";
import { cn } from "../lib/utils";

// Settings options
const DATE_FORMATS = [
  { value: "DD/MM/YYYY", label: "DD/MM/YYYY (European)" },
  { value: "MM/DD/YYYY", label: "MM/DD/YYYY (US)" },
  { value: "YYYY-MM-DD", label: "YYYY-MM-DD (ISO)" },
];

const TIME_FORMATS = [
  { value: "24-hour", label: "24-hour (14:30)" },
  { value: "12-hour", label: "12-hour (2:30 PM)" },
];

const NUMBER_FORMATS = [
  { value: "1,234.56", label: "1,234.56 (US/UK)" },
  { value: "1.234,56", label: "1.234,56 (Europe)" },
  { value: "1 234,56", label: "1 234,56 (France)" },
];

const MODE_OPTIONS = [
  { value: "system", label: "System", icon: Monitor },
  { value: "light", label: "Light", icon: Sun },
  { value: "dark", label: "Dark", icon: Moon },
];

const FONT_SCALE_OPTIONS = [
  { value: "100%", label: "100% (Default)" },
  { value: "110%", label: "110% (Larger)" },
  { value: "120%", label: "120% (Largest)" },
];

// Preference field component with override indicator
function PreferenceField({ 
  label, 
  description, 
  icon: Icon, 
  value, 
  platformDefault,
  hasOverride,
  onClear,
  children,
  saving,
  testId,
}) {
  return (
    <div 
      className="grid gap-3 p-4 rounded-lg bg-surface-elevated/30 border border-border/50"
      data-testid={testId}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
          <Label className="font-medium">{label}</Label>
          {hasOverride && (
            <Badge 
              variant="outline" 
              className="bg-primary/10 text-primary text-[10px] px-1.5 py-0"
              data-testid={testId ? `${testId}-override-badge` : undefined}
            >
              Override
            </Badge>
          )}
        </div>
        {hasOverride && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClear}
            disabled={saving}
            className="h-7 text-xs text-muted-foreground hover:text-foreground gap-1"
            data-testid={testId ? `${testId}-clear-btn` : undefined}
          >
            <RotateCcw className="h-3 w-3" />
            Use default
          </Button>
        )}
      </div>
      {description && (
        <p className="text-xs text-muted-foreground -mt-1">{description}</p>
      )}
      <div className="flex items-center gap-2">
        {children}
        {!hasOverride && platformDefault && (
          <span className="text-xs text-muted-foreground">
            (Platform: {platformDefault})
          </span>
        )}
      </div>
    </div>
  );
}

// Select with "Use platform default" option
function OverridableSelect({ 
  value, 
  onValueChange, 
  options, 
  hasOverride,
  platformDefault,
  disabled,
  testId,
}) {
  const displayValue = hasOverride ? value : "default";
  
  return (
    <Select 
      value={displayValue} 
      onValueChange={(v) => onValueChange(v === "default" ? null : v)}
      disabled={disabled}
    >
      <SelectTrigger className="w-[220px]" data-testid={testId}>
        <SelectValue>
          {displayValue === "default" 
            ? `Platform default (${platformDefault})` 
            : options.find(o => o.value === value)?.label || value
          }
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="default">
          <span className="flex items-center gap-2">
            <RotateCcw className="h-3 w-3" />
            Use platform default
          </span>
        </SelectItem>
        <Separator className="my-1" />
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export default function UserSettingsPage() {
  const {
    settings,
    userPreferences,
    platformSettings,
    loading,
    saving,
    updatePreference,
    clearPreference,
    resetAllPreferences,
    hasOverride,
    getOverride,
    getPlatformDefault,
  } = useUserSettings();

  // Theme context for visual theme
  const { theme, setTheme, mode, setMode, themeOptions, modeOptions } = useTheme();

  const [timezones, setTimezones] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(true);

  // Load available options
  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [tzRes, currRes] = await Promise.all([
          getAvailableTimezones(),
          getAvailableCurrencies(),
        ]);
        setTimezones(tzRes.data.timezones || []);
        setCurrencies(currRes.data.currencies || []);
      } catch (error) {
        console.error("Failed to load options:", extractErrorMessage(error));
      } finally {
        setLoadingOptions(false);
      }
    };
    loadOptions();
  }, []);

  // Count overrides - only count actual preference fields
  const PREFERENCE_FIELDS = [
    "date_format", "time_format", "timezone", "currency", "number_format",
    "theme_mode", "high_contrast_mode", "reduced_motion", "font_scale"
  ];
  
  const overrideCount = userPreferences 
    ? PREFERENCE_FIELDS.filter(field => 
        userPreferences[field] !== null && userPreferences[field] !== undefined
      ).length
    : 0;

  if (loading || loadingOptions) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header - EF-SETTINGS-CONSOLIDATION-V1: Updated title and added breadcrumb */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
            <a href="/settings" className="hover:text-foreground">Settings</a>
            <span>/</span>
            <span className="text-foreground">My Preferences</span>
          </div>
          <h1 className="text-2xl font-bold font-secondary tracking-tight flex items-center gap-2">
            <User className="h-6 w-6 text-primary" />
            My Preferences
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Personal preferences that override platform defaults
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          {overrideCount > 0 && (
            <Badge variant="outline" className="bg-primary/10 text-primary">
              {overrideCount} override{overrideCount !== 1 ? "s" : ""} active
            </Badge>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={resetAllPreferences}
            disabled={saving || overrideCount === 0}
            className="gap-2"
            data-testid="reset-all-preferences"
          >
            <RotateCcw className="h-4 w-4" />
            Reset All
          </Button>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-surface-elevated/50 border border-border rounded-lg p-4 flex items-start gap-3">
        <Eye className="h-5 w-5 text-muted-foreground mt-0.5" />
        <div>
          <p className="text-sm font-medium">How it works</p>
          <p className="text-xs text-muted-foreground mt-1">
            Your personal settings override the platform defaults. When you clear a preference,
            it falls back to the platform default set by your administrator.
          </p>
        </div>
      </div>

      {/* Localisation Section */}
      <Card data-testid="user-settings-localisation">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Globe className="h-5 w-5 text-primary" />
            Localisation
          </CardTitle>
          <CardDescription>
            Customize how dates, times, and numbers are displayed
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Date Format */}
          <PreferenceField
            label="Date Format"
            description="How dates appear across the application"
            icon={Calendar}
            hasOverride={hasOverride("date_format")}
            onClear={() => clearPreference("date_format")}
            platformDefault={getPlatformDefault("date_format")}
            saving={saving}
            testId="pref-date-format"
          >
            <OverridableSelect
              value={settings?.date_format}
              onValueChange={(v) => updatePreference("date_format", v)}
              options={DATE_FORMATS}
              hasOverride={hasOverride("date_format")}
              platformDefault={getPlatformDefault("date_format")}
              disabled={saving}
              testId="select-date-format"
            />
          </PreferenceField>

          {/* Time Format */}
          <PreferenceField
            label="Time Format"
            description="12-hour or 24-hour clock"
            icon={Clock}
            hasOverride={hasOverride("time_format")}
            onClear={() => clearPreference("time_format")}
            platformDefault={getPlatformDefault("time_format")}
            saving={saving}
            testId="pref-time-format"
          >
            <OverridableSelect
              value={settings?.time_format}
              onValueChange={(v) => updatePreference("time_format", v)}
              options={TIME_FORMATS}
              hasOverride={hasOverride("time_format")}
              platformDefault={getPlatformDefault("time_format")}
              disabled={saving}
              testId="select-time-format"
            />
          </PreferenceField>

          {/* Timezone */}
          <PreferenceField
            label="Display Timezone"
            description="Timezone for displaying dates and times"
            icon={Globe}
            hasOverride={hasOverride("timezone")}
            onClear={() => clearPreference("timezone")}
            platformDefault={getPlatformDefault("timezone")}
            saving={saving}
            testId="pref-timezone"
          >
            <Select
              value={hasOverride("timezone") ? getOverride("timezone") : "default"}
              onValueChange={(v) => updatePreference("timezone", v === "default" ? null : v)}
              disabled={saving}
            >
              <SelectTrigger className="w-[280px]" data-testid="select-timezone">
                <SelectValue>
                  {hasOverride("timezone")
                    ? timezones.find(t => t.value === getOverride("timezone"))?.label || getOverride("timezone")
                    : `Platform default (${getPlatformDefault("timezone")})`
                  }
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="default">
                  <span className="flex items-center gap-2">
                    <RotateCcw className="h-3 w-3" />
                    Use platform default
                  </span>
                </SelectItem>
                <Separator className="my-1" />
                {timezones.map((tz) => (
                  <SelectItem key={tz.value} value={tz.value}>
                    {tz.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </PreferenceField>

          {/* Currency */}
          <PreferenceField
            label="Currency"
            description="Currency symbol and formatting"
            icon={DollarSign}
            hasOverride={hasOverride("currency")}
            onClear={() => clearPreference("currency")}
            platformDefault={getPlatformDefault("currency")}
            saving={saving}
            testId="pref-currency"
          >
            <Select
              value={hasOverride("currency") ? getOverride("currency") : "default"}
              onValueChange={(v) => updatePreference("currency", v === "default" ? null : v)}
              disabled={saving}
            >
              <SelectTrigger className="w-[220px]" data-testid="select-currency">
                <SelectValue>
                  {hasOverride("currency")
                    ? currencies.find(c => c.value === getOverride("currency"))?.label || getOverride("currency")
                    : `Platform default (${getPlatformDefault("currency")})`
                  }
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="default">
                  <span className="flex items-center gap-2">
                    <RotateCcw className="h-3 w-3" />
                    Use platform default
                  </span>
                </SelectItem>
                <Separator className="my-1" />
                {currencies.map((currency) => (
                  <SelectItem key={currency.value} value={currency.value}>
                    {currency.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </PreferenceField>

          {/* Number Format */}
          <PreferenceField
            label="Number Format"
            description="Thousand and decimal separators"
            icon={Type}
            hasOverride={hasOverride("number_format")}
            onClear={() => clearPreference("number_format")}
            platformDefault={getPlatformDefault("number_format")}
            saving={saving}
            testId="pref-number-format"
          >
            <OverridableSelect
              value={settings?.number_format}
              onValueChange={(v) => updatePreference("number_format", v)}
              options={NUMBER_FORMATS}
              hasOverride={hasOverride("number_format")}
              platformDefault={getPlatformDefault("number_format")}
              disabled={saving}
              testId="select-number-format"
            />
          </PreferenceField>
        </CardContent>
      </Card>

      {/* Theme Section */}
      <Card data-testid="user-settings-theme">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Palette className="h-5 w-5 text-primary" />
            Theme
          </CardTitle>
          <CardDescription>
            Choose your preferred color scheme and visual style
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Visual Theme */}
          <div>
            <Label className="text-sm font-medium mb-3 block">Visual Theme</Label>
            <p className="text-xs text-muted-foreground mb-3">
              Choose a color palette style
            </p>
            <div className="grid grid-cols-3 gap-2">
              {themeOptions.map((option) => {
                const isSelected = theme === option.value;
                return (
                  <Button
                    key={option.value}
                    variant={isSelected ? "default" : "outline"}
                    size="sm"
                    onClick={() => setTheme(option.value)}
                    className={cn(
                      "h-auto py-2 flex-col gap-1",
                      isSelected && "bg-primary"
                    )}
                    data-testid={`theme-btn-${option.value}`}
                  >
                    <span className="text-xs">{option.label}</span>
                  </Button>
                );
              })}
            </div>
          </div>

          <Separator />

          {/* Light/Dark Mode */}
          <div>
            <Label className="text-sm font-medium mb-3 block">Mode</Label>
            <p className="text-xs text-muted-foreground mb-3">
              Light, dark, or follow system preference
            </p>
            <div className="flex gap-2">
              {MODE_OPTIONS.map((option) => {
                const isSelected = mode === option.value;
                const Icon = option.icon;
                return (
                  <Button
                    key={option.value}
                    variant={isSelected ? "default" : "outline"}
                    size="sm"
                    onClick={() => setMode(option.value)}
                    className={cn(
                      "gap-2",
                      isSelected && "bg-primary"
                    )}
                    data-testid={`mode-btn-${option.value}`}
                  >
                    <Icon className="h-4 w-4" />
                    {option.label}
                  </Button>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Accessibility Section */}
      <Card data-testid="user-settings-accessibility">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Accessibility className="h-5 w-5 text-primary" />
            Accessibility
          </CardTitle>
          <CardDescription>
            Customize the interface for your needs
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* High Contrast */}
          <PreferenceField
            label="High Contrast Mode"
            description="Increase contrast for better visibility"
            icon={Eye}
            hasOverride={hasOverride("high_contrast_mode")}
            onClear={() => clearPreference("high_contrast_mode")}
            platformDefault={getPlatformDefault("high_contrast_mode") ? "On" : "Off"}
            saving={saving}
            testId="pref-high-contrast"
          >
            <div className="flex items-center gap-3">
              <Switch
                checked={settings?.high_contrast_mode || false}
                onCheckedChange={(checked) => updatePreference("high_contrast_mode", checked)}
                disabled={saving}
                data-testid="high-contrast-switch"
              />
              <span className="text-sm text-muted-foreground">
                {settings?.high_contrast_mode ? "Enabled" : "Disabled"}
              </span>
            </div>
          </PreferenceField>

          {/* Reduced Motion */}
          <PreferenceField
            label="Reduced Motion"
            description="Minimize animations and transitions"
            icon={Monitor}
            hasOverride={hasOverride("reduced_motion")}
            onClear={() => clearPreference("reduced_motion")}
            platformDefault="Off"
            saving={saving}
            testId="pref-reduced-motion"
          >
            <div className="flex items-center gap-3">
              <Switch
                checked={settings?.reduced_motion || false}
                onCheckedChange={(checked) => updatePreference("reduced_motion", checked)}
                disabled={saving}
                data-testid="reduced-motion-switch"
              />
              <span className="text-sm text-muted-foreground">
                {settings?.reduced_motion ? "Enabled" : "Disabled"}
              </span>
            </div>
          </PreferenceField>

          {/* Font Scale */}
          <PreferenceField
            label="Font Scale"
            description="Increase text size throughout the application"
            icon={Type}
            hasOverride={hasOverride("font_scale")}
            onClear={() => clearPreference("font_scale")}
            platformDefault="100%"
            saving={saving}
            testId="pref-font-scale"
          >
            <div className="flex gap-2">
              {FONT_SCALE_OPTIONS.map((option) => {
                const isSelected = (settings?.font_scale || "100%") === option.value;
                
                return (
                  <Button
                    key={option.value}
                    variant={isSelected ? "default" : "outline"}
                    size="sm"
                    onClick={() => updatePreference("font_scale", option.value === "100%" ? null : option.value)}
                    disabled={saving}
                    className={cn(
                      "min-w-[100px]",
                      isSelected && "bg-primary"
                    )}
                    data-testid={`font-scale-${option.value}`}
                  >
                    {option.label}
                  </Button>
                );
              })}
            </div>
          </PreferenceField>
        </CardContent>
      </Card>

      {/* Preview Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Eye className="h-5 w-5 text-primary" />
            Preview
          </CardTitle>
          <CardDescription>
            See how your settings affect the display
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div className="p-3 rounded-lg bg-surface-elevated/50">
              <p className="text-xs text-muted-foreground mb-1">Date</p>
              <p className="font-mono">
                {settings?.date_format === "MM/DD/YYYY" && "01/22/2026"}
                {settings?.date_format === "DD/MM/YYYY" && "22/01/2026"}
                {settings?.date_format === "YYYY-MM-DD" && "2026-01-22"}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-surface-elevated/50">
              <p className="text-xs text-muted-foreground mb-1">Time</p>
              <p className="font-mono">
                {settings?.time_format === "12-hour" ? "2:30 PM" : "14:30"}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-surface-elevated/50">
              <p className="text-xs text-muted-foreground mb-1">Currency</p>
              <p className="font-mono">
                {settings?.currency === "EUR" && "€1.234,56"}
                {settings?.currency === "USD" && "$1,234.56"}
                {settings?.currency === "GBP" && "£1,234.56"}
                {!["EUR", "USD", "GBP"].includes(settings?.currency) && "£1,234.56"}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-surface-elevated/50">
              <p className="text-xs text-muted-foreground mb-1">Number</p>
              <p className="font-mono">
                {settings?.number_format === "1.234,56" && "1.234,56"}
                {settings?.number_format === "1 234,56" && "1 234,56"}
                {settings?.number_format !== "1.234,56" && settings?.number_format !== "1 234,56" && "1,234.56"}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
